/**
 * 全网影视热度榜（heatchart 批 2026-09-30）：每月决策日入场定格一份「当月在播/上映作品热度榜」。
 *
 * 数据口径（与 AwardSystem.allWorks 的参评三源**刻意不同**——榜单是「当下市场」，不是参评账本）：
 *  - 只收**此刻在播**的位，两道门（丙′ 2026-10-01 实装后收敛）：①开播月门控——配角戏份先拍完时
 *    status 早在杀青前就翻 airing，未到开播月（=剧杀青次月，2026-10-29 茄子定调「违约线=剧杀青、
 *    二月杀青三月开播」）⛔ 上榜；玩家位走 dramaTimelineOf.premiere，NPC 位同尺走 scriptPremiereAbsOf
 *    （10-29 补，杀青相位翻 airing ≠ 开播；旧档 job 无 shootStartAbs 走旧钟不设门）
 *    （与 advanceAiringContracts / allWorks② 同尺）；③全剧组杀青门控——同剧本还有任一在拍位
 *    （玩家 active / NPC shooting）= 剧组没散 ⛔ 上榜（玩家合法拖延时 NPC 按表开播的
 *    「主演只剩 NPC」由此拦下，v2.1 外审硬约束：门③⛔溶解）。原门②（选角期）随丙′结构性删除：
 *    NPC job 只在收口后存在，收口池已换新下架。
 *    履历行天然是播完的，⛔ 进榜。开局/空窗月 rows 为空 ⇒ 不弹窗、不播报。
 *  - 同一部剧去重按 **scriptId**（一部剧在榜上一个实体；⛔ 奖项那套 (scriptId, roleIndex) 位次口径——
 *    同剧两位在播（玩家+NPC 各演一角）在榜上合一行，主演把两边都列上）。
 *  - 热度单源 `FanSystem.workHeatOf`：与评选名单、履历页同一个数（含剧宣/客串累计封顶）。
 *    NPC 剧热度恒=底数（静态），玩家剧宣/客串是唯一能抬热度的力量 ⇒ 榜单的玩法就是
 *    「这个月的剧宣能不能把我的剧顶上去」。
 *  - 排序：热度降序 → 评分降序 → scriptId 兜底（确定性，刷新/重放⛔ 抖动）。
 *  - 「爆」= heat ≥ GameConfig.HEAT_BOOM_LINE（100，定线依据见彼处注释）。
 *
 * 展示时点（2026-09-30 用户定）：**行程结束 → 榜单弹窗 → 小助理播报 → 决策日**。
 * publishDueChart 挂在 `app.show('decision')` 入场（seenAbs 幂等）；⛔ 小助理播报（2026-10-02 用户令：
 * 榜单每月必弹、播报是重复打扰）。颁奖夜⛔ 在这条入场链里排班——
 * 评选届在「确认行程」链颁奖（批 G），剧情届/生涯最后一届当场播的批 B 特例见 app.decisionTail。
 */
import { GameState } from './GameState';
import { GameData } from '../data/GameData';
import { GameConfig } from '../core/GameConfig';
import { fanSystem } from './FanSystem';
import { normalNpcSystem } from './NormalNpcSystem';
import { scriptShootMonths, scriptAirMonths, dramaTimelineOf, scriptPremiereAbsOf } from './ScriptSystem';
import { absMonthOf } from '../data/tables/Awards';
import { fixedCastCredits } from '../data/Cast';
import { FilmographyEntry, HeatChartRow, ScriptDef, ScriptGenre } from '../data/types';

/** 榜面容量（TOP5；榜外玩家专属提示行另附，⛔ 占榜） */
const TOP_N = 5;

/** 主演位定位（剧本表 roleType 词表就这六种，见 tables 统计：男女主/男女二号/男女配角） */
const LEAD_ROLE_TYPES = new Set(['男主角', '女主角']);

export class HeatChartSystem {
    private static instance: HeatChartSystem;
    private constructor() {}
    static getInstance(): HeatChartSystem {
        if (!this.instance) this.instance = new HeatChartSystem();
        return this.instance;
    }

    /**
     * 每月决策日入场调用（app.show('decision')）：当月首次入场定格快照 + 播报入队。
     * seenAbs 幂等门：同月重入（切页签回来/关颁奖卡的续链重入）⛔ 重算、prev ⛔ 重复漂移。
     */
    publishDueChart(): void {
        const state = GameState.getInstance();
        if (!state.player) return; // 主菜单阶段无档
        const nowAbs = absMonthOf(state.player.year, state.player.month);
        const cur = state.player.heatChart;
        if (cur?.seenAbs === nowAbs) return;

        // 上月排名对照取自上一份定格快照（首月/上月空榜 ⇒ 全员「新上榜」）
        const prev = new Map<string, number>();
        for (const r of cur?.rows ?? []) prev.set(r.scriptId, r.rank);

        const rows = this.computeRows(prev);
        state.player.heatChart = { seenAbs: nowAbs, shownAbs: 0, rows };
        // ⛔ 小助理播报（2026-10-02 用户令删除）：榜单每月必弹，播报是重复打扰
    }

    /** 本月榜弹出来过吗（rows 有 TOP5 且 shownAbs 还停在别的月份 ⇒ 该弹） */
    unseenChart(): boolean {
        const c = GameState.getInstance().player?.heatChart;
        return !!c && c.rows.some(r => !r.outOfTop) && c.shownAbs !== c.seenAbs;
    }

    /** 关榜时落章：同月内再回决策页⛔ 重弹 */
    markChartShown(): void {
        const c = GameState.getInstance().player?.heatChart;
        if (c) c.shownAbs = c.seenAbs;
    }

    /** 面板渲染数据源（当月定格快照原样） */
    current() {
        return GameState.getInstance().player?.heatChart ?? null;
    }

    // ===== 计算（纯读运行时态，不 roll 随机 ⇒ 同状态重算恒同榜） =====

    /** 汇总在播+长尾位：scriptId → { 各角色位的演员名, 玩家是否沾边, 评分判据, 剧级尾月（锚全剧最晚完播月） } */
    private collectAiring(): Map<string, { performers: Map<number, string>; mine: boolean; score: number; tailK: number }> {
        const state = GameState.getInstance();
        const nowAbs = absMonthOf(state.player.year, state.player.month);
        const byScript = new Map<string, { performers: Map<number, string>; mine: boolean; score: number; tailK: number }>();
        const bucketOf = (scriptId: string, baseScore: number) => {
            let b = byScript.get(scriptId);
            if (!b) { b = { performers: new Map(), mine: false, score: baseScore, tailK: 0 }; byScript.set(scriptId, b); }
            return b;
        };

        // ① 玩家在播合同（评分用杀青快照，与评选同尺）。
        // 开播月门控（2026-10-01 用户实测「还没开播的影视都上榜了」；2026-10-29 二次定调）：开播月整月
        // =待播宣传月，剧在开播月**月末**才上线（=名气首滴时点）——榜单在决策日（月初）定格，那时剧
        // 还没上线，开播月⛔上榜，**开播次月**起才是上线后的第一次定格。配角戏份先拍完时 status 早在
        // 杀青前就翻 'airing'，同样拦在这里（同剧同上榜）。
        // NPC 位同尺——见 ②。
        for (const c of state.player.contracts) {
            if (c.status !== 'airing') continue;
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script?.roles[c.roleIndex]) continue;
            // 开播门控（2026-10-27 日历化批）：一律读 dramaTimelineOf，⛔ 不再就地拼公式
            if (nowAbs <= dramaTimelineOf(c, script, nowAbs).premiere) continue;
            const b = bucketOf(c.scriptId, script.baseScore);
            b.performers.set(c.roleIndex, state.player.name);
            b.mine = true;
            b.score = c.scoreSnapshot ?? script.baseScore;
        }
        // ② NPC 在播位（玩家位先占的角⛔ 覆盖——同一角只可能一个人演，revokeRole 已保证）
        for (const npc of normalNpcSystem.getNpcs()) {
            for (const j of npc.activeJobs ?? []) {
                if (j.phase !== 'airing') continue;
                const script = GameData.SCRIPT_MAP[j.scriptId];
                if (!script?.roles[j.roleIndex]) continue;
                // 开播次月门（2026-10-29 甲案，与玩家位门①同尺）：新钟 job 在名义杀青月就翻 airing
                // （advanceMonthJobs 相位机），开播月整月=待播宣传月（剧在月末首滴时才上线）⛔ 上榜，
                // nowAbs > 开播月才算上线在播。旧档 job 无 shootStartAbs 走旧钟（杀青即开播），不设门。
                if (j.shootStartAbs != null && nowAbs <= scriptPremiereAbsOf(j.shootStartAbs, script)) continue;
                const b = bucketOf(j.scriptId, script.baseScore);
                if (!b.performers.has(j.roleIndex)) b.performers.set(j.roleIndex, npc.name);
            }
        }
        // ③ 长尾源（2026-10-01 用户令「完播不掉榜，热度慢慢衰减」）。2026-10-27 改**剧级长尾**
        //    （茄子实测《一九九六的雪》主演栏空抓虫）：旧实现每条履历各自算尾月（自己完播月起算
        //    TAIL 个月），多班底剧会「先播完的先掉名」——男二（NPC·二番，该剧唯一可上主演栏的位）
        //    长尾先到期，五番玩家的长尾还拖着剧挂榜，主演解析（只认主角位+二番位）便空了。
        //    改为：完播月 = 该剧全部履历的最晚完播月（最后一位演员播完 = 剧完播），尾月 k 与
        //    衰减都锚它；剧还在尾窗内时，**同班底**条目全部回填主演名（名字随剧不随条目）。
        //    同班底判据 = 完播月 ≥ 最晚完播月 − 制作窗（拍摄期+播出期）：同一次制作的完播只会
        //    散布在这一窗内，更早的条目属于同剧旧一轮重拍（901《颠倒姻缘》二轮形态），
        //    ⛔ 给新一轮混入旧班底/旧 mine。
        if (GameConfig.HEAT_TAIL_MONTHS > 0) {
            const tails = new Map<string, { roleIndex: number; name: string; isPlayer: boolean; endAbs: number; score?: number }[]>();
            const gather = (filmography: FilmographyEntry[] | undefined, name: string, isPlayer: boolean): void => {
                for (const f of filmography ?? []) {
                    if (!GameData.SCRIPT_MAP[f.scriptId]) continue;
                    const list = tails.get(f.scriptId) ?? [];
                    list.push({ roleIndex: f.roleIndex, name, isPlayer, endAbs: absMonthOf(f.year, f.month), score: f.score });
                    tails.set(f.scriptId, list);
                }
            };
            gather(state.player.filmography, state.player.name, true);
            for (const npc of normalNpcSystem.getNpcs()) gather(npc.filmography, npc.name, false);
            for (const [scriptId, entries] of tails) {
                const script = GameData.SCRIPT_MAP[scriptId];
                const maxEnd = Math.max(...entries.map(e => e.endAbs));
                const k = nowAbs - maxEnd;
                if (k < 1 || k > GameConfig.HEAT_TAIL_MONTHS) continue;
                const productionWindow = scriptShootMonths(script) + scriptAirMonths(script);
                const cast = entries.filter(e => e.endAbs >= maxEnd - productionWindow);
                const b = bucketOf(scriptId, script.baseScore);
                b.tailK = b.tailK === 0 ? k : Math.min(b.tailK, k);
                if (cast.some(e => e.isPlayer)) b.mine = true;
                const latest = cast.find(e => e.endAbs === maxEnd);
                if (latest?.score) b.score = latest.score;
                for (const e of cast) if (!b.performers.has(e.roleIndex)) b.performers.set(e.roleIndex, e.name);
            }
        }
        return byScript;
    }

    /**
     * 主演解析：男主角/女主角位全上（番位序），不足 2 人按番位补配角；解析不到的人位不占名。
     * 固定阵容（cast）与 roles 同排：cast 的演员名从 fixedCastCredits 取，键用负索引（-1 起），
     * 与 roles 的 roleIndex 不撞；剧本角色名（如 萧燕燕）不占主演栏，主演栏永远是演员名。
     */
    private resolveStars(script: ScriptDef, performers: Map<number, string>): string[] {
        const byBilling = [
            ...script.roles.map((r, i) => ({ roleType: r.roleType, billing: r.billing, key: i })),
            ...fixedCastCredits(script).map((c, i) => ({ roleType: c.roleType, billing: c.billing, key: -(i + 1) })),
        ].sort((a, b) => (a.billing ?? 99) - (b.billing ?? 99)); // 无番位（综艺）排最后
        const leads = byBilling.filter(x => LEAD_ROLE_TYPES.has(x.roleType));

        const stars: string[] = [];
        const tryPush = (key: number): void => {
            const name = performers.get(key);
            if (name && !stars.includes(name)) stars.push(name);
        };
        for (const x of leads) tryPush(x.key);                      // 有几个主角显示几个
        if (stars.length < 2) {                                     // 只有一个主角 ⇒ 补**二番配角**
            // 字面口径（2026-10-01 用户实测打回「小角色不该出现在主演」）：只认 billing=2 的位，
            // ⛔ 顺延三番开外的小角色；二番没解析到人就宁缺毋滥
            for (const x of byBilling) {
                if (stars.length >= 2) break;
                if (LEAD_ROLE_TYPES.has(x.roleType) || x.billing !== 2) continue;
                tryPush(x.key);
            }
        }
        return stars;
    }

    private computeRows(prev: Map<string, number>): HeatChartRow[] {
        const state = GameState.getInstance();
        // 全剧组杀青门控（门③，唯一保留的在播门——v2.1 外审硬约束）：同剧本还有任一在拍位
        // （玩家 active 合同 / NPC shooting 位）= 剧组没散 ⛔ 上榜。玩家合法拖延拍摄周时 NPC 按表
        // 开播的「主演只剩 NPC」形态由此拦下。丙′ 后门②（选角期）结构性死亡：NPC job 只在
        // 收口后存在，收口池已换新下架（bindContractCast 同夜绑定归属收口池、不在新货架）。
        const shooting = new Set<string>();
        for (const c of state.player.contracts) if (c.status === 'active') shooting.add(c.scriptId);
        for (const npc of normalNpcSystem.getNpcs())
            for (const j of npc.activeJobs ?? [])
                if (j.phase === 'shooting') shooting.add(j.scriptId);
        const byScript = this.collectAiring();
        // 固定阵容只补到已成立的在播/长尾桶：本剧没有任何真实在播位则桶不存在，不凭空造剧。
        // 负索引 -1 起与 roles 的 roleIndex 错开；演员名从信用口取，不许用角色名顶主演。
        for (const [scriptId, b] of byScript) {
            const script = GameData.SCRIPT_MAP[scriptId];
            if (!script) continue;
            fixedCastCredits(script).forEach((c, i) => {
                const key = -(i + 1);
                if (!b.performers.has(key)) b.performers.set(key, c.actorName);
            });
        }
        const all = [...byScript.entries()]
            .filter(([scriptId]) => !shooting.has(scriptId))
            .map(([scriptId, b]) => {
            const script = GameData.SCRIPT_MAP[scriptId];
            // 长尾衰减（2026-10-01 用户令）：完播剧 tailK>0，热度 = 完播时热度 × DECAY^tailK；
            // 「爆」按**当月显示热度**判（2026-10-02 用户拍板：衰减跌破爆线即摘标，⛔ 保留历史爆）
            const base = fanSystem.workHeatOf(script);
            const heat = b.tailK > 0
                ? Math.round(base * Math.pow(GameConfig.HEAT_TAIL_DECAY, b.tailK))
                : base;
            return {
                scriptId,
                genre: script.genre as ScriptGenre,
                heat,
                boom: heat >= GameConfig.HEAT_BOOM_LINE,
                score: b.score,
                mine: b.mine,
                stars: this.resolveStars(script, b.performers),
                prevRank: prev.get(scriptId) ?? 0,
            };
        });
        // 热度降序 → 评分降序 → scriptId 兜底（确定性）
        all.sort((a, b) =>
            b.heat - a.heat || b.score - a.score || (a.scriptId < b.scriptId ? -1 : a.scriptId > b.scriptId ? 1 : 0));

        const rows: HeatChartRow[] = all.slice(0, TOP_N).map((w, i) => ({
            scriptId: w.scriptId,
            rank: i + 1,
            genre: w.genre,
            heat: w.heat,
            boom: w.heat >= GameConfig.HEAT_BOOM_LINE,
            stars: w.stars,
            mine: w.mine,
            prevRank: w.prevRank,
        }));
        // 榜外玩家专属行：真实名次附在榜尾（多部同时在播且都榜外就都给，⛔ 挤占 TOP5）
        all.forEach((w, i) => {
            if (w.mine && i >= TOP_N) {
                rows.push({
                    scriptId: w.scriptId,
                    rank: i + 1,
                    genre: w.genre,
                    heat: w.heat,
                    boom: w.heat >= GameConfig.HEAT_BOOM_LINE,
                    stars: w.stars,
                    mine: true,
                    prevRank: w.prevRank,
                    outOfTop: true,
                });
            }
        });
        return rows;
    }
}

/** 导出单例 */
export const heatChartSystem = HeatChartSystem.getInstance();
